import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ISlide} from '../../incase-domain/slide';
import {ICanvasLine} from '../../incase-domain/proptypes/runfile';
import {savePresentationRunFileAction} from '../../store/actions/runfile.actions';
import {getElementBodyOffset} from '../../util/util';
import {
    currentSlideIndexSelector,
    drawEnabledSelector,
    redrawFlagSelector,
    visibleSlideSelector,
} from '../../store/selectors/presentation.selectors';
import {runFileSelector} from '../../store/selectors/runfile.selectors';
import {shouldSkipSample, simplifyCanvasLine} from './slideDrawingCompression';

export function useSlideDrawing(slide: ISlide): {
    canvasRef: React.RefObject<HTMLCanvasElement>;
    drawEnabled: boolean;
    handlers: {
        onMouseDown: React.MouseEventHandler<HTMLDivElement>;
        onMouseMove: React.MouseEventHandler<HTMLDivElement>;
        onMouseLeave: React.MouseEventHandler<HTMLDivElement>;
        onMouseUp: React.MouseEventHandler<HTMLDivElement>;
        onTouchStart: React.TouchEventHandler<HTMLDivElement>;
        onTouchMove: React.TouchEventHandler<HTMLDivElement>;
        onTouchEnd: React.TouchEventHandler<HTMLDivElement>;
        onTouchCancel: React.TouchEventHandler<HTMLDivElement>;
    };
} {
    const dispatch = useDispatch();
    const slides = useSelector(visibleSlideSelector);
    const currentSlideIndex = useSelector(currentSlideIndexSelector);
    const drawEnabled = useSelector(drawEnabledSelector);
    const redrawFlag = useSelector(redrawFlagSelector);
    const runFile = useSelector(runFileSelector);
    const runFileSlideDrawLines = runFile.slideDrawLines;

    const canvasRef = useRef<HTMLCanvasElement>(null);

    const currentLineRef = useRef<ICanvasLine>({clickX: [], clickY: []});
    const doPaintRef = useRef<boolean>(false);
    const lastCaptureMsRef = useRef<number>(0);

    const slideDrawLines = useMemo(() => {
        // if this slide is showed in modal dont draw currentSlideIndex slide drawings
        const isCurrentSlideSelected = slides && slides[currentSlideIndex]._id === slide._id;

        if (
            isCurrentSlideSelected &&
            runFileSlideDrawLines &&
            runFileSlideDrawLines.length > 0 &&
            runFileSlideDrawLines.length > currentSlideIndex
        ) {
            return runFileSlideDrawLines[currentSlideIndex] || [];
        }
        return [];
    }, [runFileSlideDrawLines, currentSlideIndex, slides, slide._id]);

    const contextPromise = useCallback(() => {
        let context;
        if (canvasRef.current) {
            context = canvasRef.current.getContext('2d');

            if (context) {
                context.strokeStyle = '#df4b26';
                context.lineJoin = 'round';
                context.lineWidth = 2.5;
            }
        }
        return context;
    }, []);

    const drawLine = useCallback((context, line) => {
        if (line.clickX.length > 1) {
            context.beginPath();
            context.moveTo(line.clickX[0], line.clickY[0]);
            for (let i = 1; i < line.clickX.length - 1; i++) {
                context.lineTo(line.clickX[i + 1], line.clickY[i + 1]);
                context.moveTo(line.clickX[i + 1], line.clickY[i + 1]);
            }
            context.closePath();
            context.stroke();
        }
    }, []);

    const addClick = useCallback((x: number, y: number) => {
        currentLineRef.current.clickX.push(x);
        currentLineRef.current.clickY.push(y);
        currentLineRef.current.canvasWidth = canvasRef.current?.width;
        currentLineRef.current.canvasHeight = canvasRef.current?.height;
    }, []);

    const doUpdateFile = useCallback(
        (newLines) => {
            let newSlideDrawLines = [...(runFileSlideDrawLines || [])];
            newSlideDrawLines[currentSlideIndex] = [...slideDrawLines, ...newLines];
            newSlideDrawLines = newSlideDrawLines.map((line) => line || []);
            dispatch(savePresentationRunFileAction({...runFile, slideDrawLines: newSlideDrawLines}));
        },
        [runFile, runFileSlideDrawLines, slideDrawLines, currentSlideIndex, dispatch]
    );

    const redraw = useCallback(() => {
        const context = contextPromise();
        if (context) {
            context.clearRect(0, 0, context.canvas.width, context.canvas.height);
            Object.values([...slideDrawLines]).forEach((line) => {
                context.save();
                if (!line.canvasWidth || !line.canvasHeight || !canvasRef.current) return;

                const scaleX = line.canvasWidth ? (canvasRef.current.clientWidth || 0) / line.canvasWidth : 1;
                const scaleY = line.canvasHeight ? (canvasRef.current.clientHeight || 0) / line.canvasHeight : 1;

                context.scale(scaleX, scaleY);

                drawLine(context, line);

                context.restore();
            });
        }
    }, [contextPromise, drawLine, slideDrawLines]);

    useEffect(() => {
        redraw();
    }, [canvasRef.current?.clientWidth, canvasRef.current?.clientHeight, redraw, currentSlideIndex, redrawFlag]);

    const startDrawing = useCallback(() => {
        if (drawEnabled) {
            doPaintRef.current = true;
            lastCaptureMsRef.current = 0;
        }
    }, [drawEnabled]);

    const draw = useCallback(
        (event) => {
            if (!drawEnabled || !doPaintRef.current) return;
            const now = performance.now();
            if (shouldSkipSample(lastCaptureMsRef.current, now)) return;

            const lastX = currentLineRef.current.clickX[currentLineRef.current.clickX.length - 1];
            const lastY = currentLineRef.current.clickY[currentLineRef.current.clickY.length - 1];
            const dist = Math.hypot(event.nativeEvent.layerX - lastX, event.nativeEvent.layerY - lastY);

            if (currentLineRef.current.clickX.length > 0 && dist < 2) return;

            lastCaptureMsRef.current = now;
            addClick(event.nativeEvent.layerX, event.nativeEvent.layerY);
            drawLine(contextPromise(), currentLineRef.current);
        },
        [addClick, drawEnabled, contextPromise, drawLine]
    );

    const stopDrawing = useCallback(() => {
        if (!drawEnabled || !doPaintRef.current) return;
        const finishedLine = currentLineRef.current;
        currentLineRef.current = {clickX: [], clickY: []};
        doPaintRef.current = false;
        doUpdateFile([simplifyCanvasLine(finishedLine)]);
    }, [drawEnabled, doUpdateFile]);

    const drawTouch = useCallback(
        (event) => {
            if (!drawEnabled || !doPaintRef.current) return;
            const now = performance.now();
            if (shouldSkipSample(lastCaptureMsRef.current, now)) return;
            lastCaptureMsRef.current = now;

            const offset = getElementBodyOffset(canvasRef.current as HTMLElement);
            [...event.changedTouches].forEach((touch) => {
                addClick(touch.clientX - offset.left, touch.clientY - offset.top);
            });

            drawLine(contextPromise(), currentLineRef.current);
        },
        [addClick, drawEnabled, contextPromise, drawLine]
    );

    const handlers = useMemo(
        () => ({
            onMouseDown: startDrawing,
            onMouseMove: draw,
            onMouseLeave: stopDrawing,
            onMouseUp: stopDrawing,
            onTouchStart: startDrawing,
            onTouchMove: drawTouch,
            onTouchEnd: stopDrawing,
            onTouchCancel: stopDrawing,
        }),
        [startDrawing, draw, drawTouch, stopDrawing]
    );

    return {
        canvasRef,
        drawEnabled,
        handlers,
    };
}
